import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { RocketLaunchIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
import type { WhatsNewSlide } from './types';
import { clampStep, isFinalStep, nextStep, previousStep } from './whatsNewSteps';

export interface WhatsNewModalProps {
    open: boolean;
    slides: WhatsNewSlide[];
    onDismiss: () => void;
    title?: string;
    finalLabel?: string;
    /** Escape and a close button dismiss too; off, only the final button does. */
    dismissible?: boolean;
    /** Overrides the close button alone, so Escape can dismiss with no visible X. */
    showCloseButton?: boolean;
    /** Holds one box size on every slide instead of growing with the slide. */
    fixedSize?: boolean;
}

const DEFAULT_FINAL_LABEL = 'Get Started';
const FLUID_BODY = 'min-h-[420px]';
// 560px is the welcome slide's natural height; the cap keeps the footer on short screens.
const FIXED_DIALOG_SIZE = 'h-[560px] max-h-[calc(100vh-2rem)]';
const FIXED_BODY = 'flex-1 min-h-0 overflow-y-auto';

function portalTarget(): HTMLElement {
    return document.getElementById('modal-root') ?? document.body;
}

interface HeaderProps {
    title?: string;
    titleId: string;
    onClose?: () => void;
}

const WhatsNewHeader: React.FC<HeaderProps> = ({ title, titleId, onClose }) => (
    <div className="flex items-center justify-between gap-4 px-8 pt-6">
        {title ? (
            <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                {title}
            </h2>
        ) : (
            <span />
        )}
        {onClose && (
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
                <XMarkIcon className="w-5 h-5" />
            </button>
        )}
    </div>
);

interface FooterProps {
    step: number;
    total: number;
    finalLabel: string;
    onBack: () => void;
    onNext: () => void;
}

const WhatsNewFooter: React.FC<FooterProps> = ({ step, total, finalLabel, onBack, onNext }) => {
    const isLast = isFinalStep(step, total);
    return (
        <div className="flex items-center justify-between px-8 py-6 border-t border-gray-100">
            <div className="flex items-center gap-4">
                {/* Hidden on the first slide rather than disabled, so no control does nothing. */}
                {step > 0 && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Back
                    </button>
                )}
                <span className="text-xs text-gray-400">
                    {step + 1} of {total}
                </span>
            </div>
            <button
                type="button"
                onClick={onNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-black text-white font-semibold text-sm rounded-xl shadow hover:bg-gray-900 transition-colors"
            >
                {isLast ? (
                    <>
                        {finalLabel}
                        <RocketLaunchIcon className="w-4 h-4" />
                    </>
                ) : (
                    <>
                        Next
                        <ArrowRightIcon className="w-4 h-4" />
                    </>
                )}
            </button>
        </div>
    );
};

export const WhatsNewModal: React.FC<WhatsNewModalProps> = ({
    open,
    slides,
    onDismiss,
    title,
    finalLabel = DEFAULT_FINAL_LABEL,
    dismissible = true,
    showCloseButton = dismissible,
    fixedSize = false
}) => {
    const [requestedStep, setRequestedStep] = useState(0);
    const titleId = useId();
    const total = slides.length;

    useEscapeKey(onDismiss, open && dismissible);

    const goBack = useCallback(() => setRequestedStep((s) => previousStep(s, total)), [total]);
    const goNext = useCallback(() => setRequestedStep((s) => nextStep(s, total)), [total]);

    if (!open || total === 0) return null;

    const step = clampStep(requestedStep, total);
    const slide = slides[step];
    const handleNext = isFinalStep(step, total) ? onDismiss : goNext;
    const showHeader = Boolean(title) || showCloseButton;
    const sizeClass = fixedSize ? FIXED_DIALOG_SIZE : '';
    const bodyClass = fixedSize ? FIXED_BODY : FLUID_BODY;

    const modalContent = (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-label={title ? undefined : slide.label}
                className={`bg-white rounded-xl shadow-sm w-full max-w-5xl mx-4 flex flex-col overflow-hidden ${sizeClass}`}
                onClick={(e) => e.stopPropagation()}
            >
                {showHeader && (
                    <WhatsNewHeader
                        title={title}
                        titleId={titleId}
                        onClose={showCloseButton ? onDismiss : undefined}
                    />
                )}
                {/* Only the current slide renders, so slide media never loads ahead of time. */}
                <div className={`flex items-center justify-center px-8 py-8 ${bodyClass}`}>
                    {slide.content}
                </div>
                <WhatsNewFooter
                    step={step}
                    total={total}
                    finalLabel={finalLabel}
                    onBack={goBack}
                    onNext={handleNext}
                />
            </div>
        </div>
    );

    return createPortal(modalContent, portalTarget());
};

export default WhatsNewModal;
